import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Sparkles, Zap, TrendingUp, Shield, Brain, BarChart3, Activity, ArrowRight, Bot, LineChart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
const Landing = () => {
  const {
    user
  } = useAuth();
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState<{
    [key: string]: boolean;
  }>({});
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Redirect to app if already authenticated
  useEffect(() => {
    if (user) {
      navigate('/app/overview');
    }
  }, [user, navigate]);
  useEffect(() => {
    observerRef.current = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setIsVisible(prev => ({
            ...prev,
            [entry.target.id]: true
          }));
        }
      });
    }, {
      threshold: 0.1
    });
    document.querySelectorAll('[data-animate]').forEach(el => {
      if (observerRef.current) observerRef.current.observe(el);
    });
    return () => observerRef.current?.disconnect();
  }, []);
  return <div className="min-h-screen bg-[#111111] text-white overflow-hidden">
      {/* Enhanced Background */}
      <div className="fixed inset-0 grid-pattern" />
      <div className="fixed inset-0" style={{
      background: 'radial-gradient(circle at 25% 10%, rgba(255, 255, 255, 0.03) 0%, transparent 40%), radial-gradient(circle at 75% 75%, rgba(255, 255, 255, 0.03) 0%, transparent 40%)'
    }} />
      
      {/* Enhanced Gradient Orbs */}
      <div className="fixed top-0 left-1/4 w-[600px] h-[600px] bg-primary/10 rounded-full blur-[150px] opacity-30" />
      <div className="fixed bottom-0 right-1/4 w-[600px] h-[600px] bg-accent/10 rounded-full blur-[150px] opacity-30" />
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] bg-white/5 rounded-full blur-[120px] opacity-20" />

      <div className="relative">
        {/* Hero Section */}
        <section className="container mx-auto px-4 pt-20 pb-32">
          <div className="max-w-6xl mx-auto text-center">
            {/* Premium Status Badge */}
            <div className="inline-flex items-center gap-3 glass-card glow-border px-5 py-2.5 mb-10 fade-in-up opacity-0 rounded-full">
              <div className="relative">
                <div className="w-2.5 h-2.5 bg-primary rounded-full" />
                <div className="absolute inset-0 bg-primary rounded-full animate-ping" style={{
                animationDuration: '2s'
              }} />
              </div>
              <span className="text-sm text-white font-medium tracking-wide">
                AI-Powered Crypto Analytics
              </span>
            </div>

            {/* Enhanced Main Title */}
            <h1 className="text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold tracking-tight mb-8 fade-in-up opacity-0 delay-100">
              <span className="text-gradient block mb-2">Crystos AI</span>
              <span className="text-white/90 block text-3xl md:text-4xl lg:text-5xl font-normal">
                Inteligência Artificial para Cripto Trading
              </span>
              <div className="w-32 h-0.5 bg-gradient-to-r from-transparent via-primary to-transparent mx-auto mt-6" />
            </h1>

            {/* Enhanced Subtitle */}
            <p className="text-lg md:text-xl lg:text-2xl text-white/70 max-w-3xl mx-auto mb-12 fade-in-up opacity-0 delay-200 leading-relaxed">
              Análise avançada de mercado, alertas em tempo real e estratégias automatizadas 
              powered by AI para maximizar seus retornos no mercado cripto.
            </p>

            {/* Premium CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-6 justify-center mb-20 fade-in-up opacity-0 delay-300">
              <Link to="/auth">
                <button className="premium-button dots-border shimmer group">
                  <span className="relative z-10 flex items-center gap-3 text-base font-semibold text-white">
                    Começar Agora
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </span>
                </button>
              </Link>
              
              <Link to="/app/overview">
                <Button size="lg" variant="outline" className="glass-card px-8 py-6 text-base font-semibold border-white/20 hover:bg-white/10 group rounded-full">
                  <Sparkles className="w-5 h-5 mr-2 group-hover:scale-110 group-hover:rotate-12 transition-all" />
                  Explorar Demo
                </Button>
              </Link>
            </div>

            {/* Enhanced Feature Cards Grid */}
            <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {[{
              icon: Activity,
              title: 'Análise em Tempo Real',
              description: 'Monitore mercados 24/7 com dados atualizados'
            }, {
              icon: Brain,
              title: 'IA Avançada',
              description: 'Insights preditivos e recomendações inteligentes'
            }, {
              icon: Shield,
              title: 'Seguro & Confiável',
              description: 'Seus dados protegidos com criptografia'
            }].map((feature, index) => <div key={index} className={`glass-card p-8 group transition-all duration-300 fade-in-up opacity-0 delay-${(index + 4) * 100}`}>
                  <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform mx-auto">
                    <feature.icon className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="text-white font-semibold text-lg mb-3">{feature.title}</h3>
                  <p className="text-white/60 text-sm leading-relaxed">{feature.description}</p>
                  <div className="gradient-line text-primary mt-6 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>)}
            </div>
          </div>
        </section>

        {/* Enhanced Features Section */}
        <section className="container mx-auto px-4 py-24">
          <div className="max-w-7xl mx-auto">
            {/* Premium Section Badge */}
            <div id="features-badge" data-animate className={`inline-flex items-center gap-3 glass-card glow-border px-5 py-2.5 mb-8 rounded-full ${isVisible['features-badge'] ? 'fade-in-up' : 'opacity-0'}`}>
              <Zap className="w-5 h-5 text-primary" />
              <span className="text-sm text-white/90 font-medium tracking-wide">Recursos Poderosos</span>
            </div>

            <h2 id="features-title" data-animate className={`text-4xl md:text-5xl lg:text-6xl font-bold mb-16 ${isVisible['features-title'] ? 'fade-in-up' : 'opacity-0'}`}>
              <span className="text-gradient block mb-2">Tudo que você precisa</span>
              <span className="text-white/90 block">em uma única plataforma</span>
              <div className="w-24 h-0.5 bg-gradient-to-r from-transparent via-primary to-transparent mt-6" />
            </h2>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[{
              icon: TrendingUp,
              title: 'Market Intelligence',
              description: 'Análise profunda de tendências, volume, liquidez e sentimento de mercado em tempo real.'
            }, {
              icon: Bot,
              title: 'AI Advisor',
              description: 'Sistema de IA que analisa dados on-chain e sugere estratégias personalizadas de trading.'
            }, {
              icon: BarChart3,
              title: 'Backtesting Avançado',
              description: 'Teste suas estratégias com dados históricos e otimize parâmetros automaticamente.'
            }, {
              icon: Shield,
              title: 'Risk Management',
              description: 'Ferramentas de gestão de risco com alertas automáticos e stop-loss inteligente.'
            }, {
              icon: LineChart,
              title: 'Alertas TradingView',
              description: 'Integração completa com TradingView para alertas automatizados via Telegram.'
            }, {
              icon: Sparkles,
              title: 'Alpha On-Chain',
              description: 'Métricas exclusivas de blockchain: whales, derivativos, fear & greed e social sentiment.'
            }].map((feature, index) => <div key={index} id={`feature-${index}`} data-animate className={`glass-card p-10 group transition-all duration-300 ${isVisible[`feature-${index}`] ? 'fade-in-up' : 'opacity-0'}`}>
                  <div className="w-16 h-16 rounded-2xl bg-white/10 flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all">
                    <feature.icon className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-4">{feature.title}</h3>
                  <p className="text-white/60 leading-relaxed text-sm">{feature.description}</p>
                  <div className="gradient-line text-primary mt-8 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>)}
            </div>
          </div>
        </section>

        {/* Premium CTA Section */}
        <section className="container mx-auto px-4 py-24">
          <div id="cta-section" data-animate className={`max-w-5xl mx-auto glass-card p-16 text-center relative overflow-hidden ${isVisible['cta-section'] ? 'fade-in-up' : 'opacity-0'}`}>
            {/* Decorative glow */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-96 bg-primary/20 rounded-full blur-[100px] -z-10" />
            
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              <span className="text-gradient">Pronto para começar?</span>
            </h2>
            <p className="text-lg md:text-xl text-white/70 mb-10 max-w-2xl mx-auto leading-relaxed">
              Junte-se a milhares de traders que já estão usando IA para tomar decisões mais inteligentes no mercado cripto.
            </p>
            <Link to="/auth">
              <button className="premium-button dots-border shimmer group">
                <span className="relative z-10 flex items-center gap-3 text-lg font-bold text-white">
                  Criar Conta Grátis
                  <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
                </span>
              </button>
            </Link>
            
            {/* Stats */}
            <div className="grid grid-cols-3 gap-8 mt-16 pt-16 border-t border-white/10">
              <div>
                <div className="text-3xl md:text-4xl font-bold text-white mb-2">1000+</div>
                <div className="text-white/60 text-sm">Usuários ativos</div>
              </div>
              <div>
                <div className="text-3xl md:text-4xl font-bold text-white mb-2">99.9%</div>
                <div className="text-white/60 text-sm">Uptime</div>
              </div>
              <div>
                <div className="text-3xl md:text-4xl font-bold text-white mb-2">24/7</div>
                <div className="text-white/60 text-sm">Monitoramento</div>
              </div>
            </div>
          </div>
        </section>

        {/* Enhanced Footer */}
        <footer className="container mx-auto px-4 py-12 border-t border-white/10">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col md:flex-row justify-between items-center gap-6">
              <div className="text-center md:text-left">
                <div className="text-2xl font-bold text-gradient mb-2">Crystos AI</div>
                <p className="text-white/60 text-sm">Inteligência Artificial para Cripto Trading</p>
              </div>
              
              <div className="flex gap-8 text-sm text-white/60">
                <Link to="/app/overview" className="hover:text-white transition-colors">
                  Dashboard
                </Link>
                <Link to="/auth" className="hover:text-white transition-colors">
                  Login
                </Link>
                <a href="#features" className="hover:text-white transition-colors">
                  Recursos
                </a>
              </div>
            </div>
            
            <div className="text-center text-white/40 text-xs mt-8 pt-8 border-t border-white/5">
              <p>© 2025 Catalyista AI. Todos os direitos reservados.</p>
            </div>
          </div>
        </footer>
      </div>
    </div>;
};
export default Landing;